/**
 * KAN-744: диалог менеджера: создать или изменить (Фамилия, Имя, Отчество,
 * Телефон, Процент с продаж, Заметки). Обязательно только имя (KAN-745).
 * Только для владельца.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useManagerMutations } from '@/hooks/useManagers';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { COMMISSION_ERROR, parseCommission } from '@/lib/managerFormat';
import type { Manager } from '@/types/manager';

export const MANAGER_NAME_MAX = 100;
export const MANAGER_PHONE_MAX = 32;
export const MANAGER_NOTES_MAX = 2000;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Менеджер для изменения; нет: создание. */
  manager?: Manager | null;
  onSaved?: (manager: Manager) => void;
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  maxLength: number;
  placeholder?: string;
  inputMode?: 'text' | 'decimal';
}

function TextField({
  id,
  label,
  value,
  onChange,
  disabled,
  maxLength,
  placeholder,
  inputMode,
}: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        data-testid={id}
        inputMode={inputMode}
        value={value}
        maxLength={maxLength}
        autoComplete="off"
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function ManagerDialog({ open, onOpenChange, manager, onSaved }: Props): JSX.Element {
  const { create, update } = useManagerMutations();
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [commission, setCommission] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLastName(manager?.last_name ?? '');
    setFirstName(manager?.first_name ?? '');
    setMiddleName(manager?.middle_name ?? '');
    setPhone(manager?.phone ?? '');
    setNotes(manager?.notes ?? '');
    setCommission(
      manager?.commission_pct === null || manager?.commission_pct === undefined
        ? ''
        : String(manager.commission_pct)
    );
    setError(null);
  }, [open, manager]);

  const pending = create.isPending || update.isPending;
  const editing = !!manager;

  const submit = async (): Promise<void> => {
    if (!firstName.trim()) {
      setError('Укажите имя');
      return;
    }
    const commissionPct = parseCommission(commission);
    if (commissionPct === undefined) {
      setError(COMMISSION_ERROR);
      return;
    }
    setError(null);
    const body = {
      last_name: lastName.trim(),
      first_name: firstName.trim(),
      middle_name: middleName.trim(),
      phone: phone.trim(),
      notes: notes.trim(),
      commission_pct: commissionPct,
    };
    try {
      const saved = editing
        ? await update.mutateAsync({ id: manager.id, data: body })
        : await create.mutateAsync(body);
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      setError(extractErrorMessage(err, 'Не удалось сохранить менеджера'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manager-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Менеджер: изменить' : 'Новый менеджер'}</DialogTitle>
          <DialogDescription>
            Человек, который доводит клиентов до продажи. Закрепляется за клиентом и заказом.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <TextField
            id="manager-dialog-last-name"
            label="Фамилия"
            value={lastName}
            onChange={setLastName}
            disabled={pending}
            maxLength={MANAGER_NAME_MAX}
          />
          <TextField
            id="manager-dialog-first-name"
            label="Имя *"
            value={firstName}
            onChange={setFirstName}
            disabled={pending}
            maxLength={MANAGER_NAME_MAX}
          />
          <TextField
            id="manager-dialog-middle-name"
            label="Отчество"
            value={middleName}
            onChange={setMiddleName}
            disabled={pending}
            maxLength={MANAGER_NAME_MAX}
          />
          <TextField
            id="manager-dialog-phone"
            label="Телефон"
            value={phone}
            onChange={setPhone}
            disabled={pending}
            maxLength={MANAGER_PHONE_MAX}
            placeholder="+7..."
          />
          <TextField
            id="manager-dialog-commission"
            label="Процент с продаж, %"
            value={commission}
            onChange={setCommission}
            disabled={pending}
            maxLength={6}
            inputMode="decimal"
            placeholder="Не задан"
          />
          <div className="flex flex-col gap-1">
            <Label htmlFor="manager-dialog-notes">Заметки</Label>
            <Textarea
              id="manager-dialog-notes"
              data-testid="manager-dialog-notes"
              rows={3}
              maxLength={MANAGER_NOTES_MAX}
              value={notes}
              disabled={pending}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          {error && (
            <p className="text-xs text-red-700" data-testid="manager-dialog-error">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Отмена
          </Button>
          <Button data-testid="manager-dialog-submit" disabled={pending} onClick={submit}>
            {pending ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
